import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertTriangle,
  Boxes,
  Check,
  ExternalLink,
  FileText,
  Folder,
  Layers,
  Loader2,
  Plug,
  Scale,
  Terminal,
} from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { useTheme } from "@/shared/hooks/use-theme";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import type {
  SkillCatalogDetail,
  SkillInstallationScope,
  SkillParameter,
  SkillStorefrontEntry,
} from "../../types/skill";
import { requirementLabel } from "../../utils/requirement";
import { parseSkillFacets } from "../../utils/facets";
import { textoDaSkill } from "../../utils/text";
import { SKILL_ENTRY_FILE } from "./types";

/**
 * Um arquivo do pacote, do caminho que a vitrine em uso conhece.
 *
 * `productId` nulo é a vitrine pessoal (AB#85633), que não tem workspace — e as
 * duas rotas leem o mesmo catálogo, só com origem de escopo diferente.
 */
/**
 * Apresentação: o procedimento em si, renderizado (AC12).
 *
 * A descrição de uma linha do cartão não basta para decidir instalar. O que
 * decide é o roteiro, e é ele que aparece aqui.
 */
export const OverviewStep = ({
  detail,
  showMeta = true,
  license,
  bodyClassName = "max-h-[26vh]",
}: {
  detail: SkillCatalogDetail;
  /**
   * Mostra etiquetas, descrição e versão acima do procedimento.
   *
   * Ligado na instalação, onde o cabeçalho da janela traz só o nome. Desligado
   * na tela de configuração, cujo cabeçalho já traz os três — e repeti-los ali
   * empurrava o procedimento, que é o conteúdo da aba, para fora da primeira
   * tela.
   */
  showMeta?: boolean;
  /**
   * Como ler o arquivo de licença, quando quem chama quer a licença AQUI.
   *
   * A licença era um passo próprio. Ela continua sendo a única parte da decisão
   * que não é sobre o que a skill faz — instalar procedimento de terceiro é
   * aceitar os termos de quem o escreveu —, mas uma etapa inteira para duas
   * linhas de texto (o caso comum, em que só o nome da licença é declarado)
   * custava mais atenção do que entregava. Junto da apresentação, ela é lida no
   * mesmo fôlego do procedimento.
   *
   * Omitido, a licença não aparece: quem só mostra o procedimento não precisa
   * saber de onde se lê arquivo.
   */
  license?: {
    readFile: (path: string) => Promise<{ content: string }>;
    cacheKey: unknown[];
  };
  /**
   * Quanto o procedimento ocupa antes de rolar por dentro.
   *
   * O padrão é curto porque a apresentação nasceu dentro de janelas — instalar e
   * publicar —, onde o que vem depois dela precisa continuar visível. Numa
   * PÁGINA não há nada disputando espaço, e encolher o procedimento ali só
   * obriga a rolar duas vezes.
   */
  bodyClassName?: string;
}) => {
  const { t } = useTranslation();
  const etiquetas = parseSkillFacets(detail.facets);

  return (
    <div className="space-y-4">
      {showMeta && (
        <>
          {/* As etiquetas também na instalação (AB#85634): quem instala vê a que
              área o procedimento pertence antes de decidir, e é nesse momento que
              elas passam a existir no cadastro de facets da organização. */}
          {etiquetas.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {etiquetas.map((etiqueta) => (
                <Badge key={etiqueta.raw} variant="secondary" className="text-[11px] font-normal">
                  {etiqueta.name}: {etiqueta.value}
                </Badge>
              ))}
            </div>
          )}

          <p className="text-sm text-muted-foreground">{detail.description}</p>

          {/* Skill com scripts (AB#85642): diz onde eles rodam. Não é selo nem
              filtro — é a informação que muda a expectativa de quem instala para
              usar num agente externo. */}
          {detail.compatibility === "harness_required" && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Terminal className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t("skills.scripts.runsInProdgy")}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{t("skills.version", { version: detail.version })}</Badge>
            <Badge variant="outline">
              {detail.publisher_scope === "platform"
                ? t("skills.publisher.platform")
                : t("skills.publisher.organization")}
            </Badge>
            {/* De onde o pacote veio, quando a casa anotou. Link, e nao texto:
                o campo existe justamente para se poder IR ate a origem —
                conferir se saiu versao nova, ver quem mantem. */}
            {detail.source_url && (
              <a
                href={detail.source_url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex"
              >
                <Badge variant="outline" className="gap-1 hover:bg-accent">
                  <ExternalLink className="h-3 w-3" />
                  {t("skills.publish.sourceLink")}
                </Badge>
              </a>
            )}
          </div>
        </>
      )}

      {/* Em sanfona, e não despejado na tela: o procedimento de uma skill real
          tem páginas, e quem só quer conferir o cadastro rolava tudo isso até
          achar o resto.

          A licença vem ANTES: ela é a única parte que não é sobre o que a skill
          faz, e cabe num cabeçalho — o nome dela já aparece sem abrir nada. O
          procedimento vem depois, aberto, porque é o conteúdo da tela. */}
      <Accordion type="multiple" defaultValue={["procedure"]} className="w-full">
        {license && (detail.license || detail.license_path) && (
          <AccordionItem value="license" className="mb-3 rounded-md border px-4">
            <AccordionTrigger className="text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-muted-foreground" />
                {detail.license ?? t("skills.license.undeclared")}
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <LicenseBlock
                detail={detail}
                readFile={license.readFile}
                cacheKey={license.cacheKey}
              />
            </AccordionContent>
          </AccordionItem>
        )}

        <AccordionItem value="procedure" className="rounded-md border px-4">
          <AccordionTrigger className="text-sm font-medium hover:no-underline">
            <span className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {SKILL_ENTRY_FILE}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            {/* Rola por dentro, em altura curta: aberto o procedimento inteiro,
                a página deixava de mostrar que existe qualquer coisa depois
                dele — e num pacote grande são muitas telas de rolagem até o
                próximo campo.

                `prose` do @tailwindcss/typography, e nao a classe
                `markdown-content` usada no resto do app: aquela classe nunca foi
                definida em CSS nenhum, entao o reset do Tailwind zera os titulos
                e o procedimento sai achatado, sem hierarquia. `break-words`
                evita a barra horizontal que URLs longas do pacote provocam. */}
            <div className={cn("scroll-slim overflow-y-auto pr-2", bodyClassName)}>
              <div className="prose prose-sm max-w-none break-words dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.body}</ReactMarkdown>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

/**
 * Licença: sob que termos esta skill pode ser usada.
 *
 * Vive dentro da apresentação, e não como passo ou aba própria — mas continua
 * sendo um bloco destacado, e não uma linha perdida na árvore de arquivos:
 * enterrar os termos entre os materiais de apoio esconderia exatamente a
 * informação que alguém pode precisar defender depois. A `pdf` de referência da
 * Anthropic, por exemplo, é proprietária.
 */
const LicenseBlock = ({
  detail,
  readFile,
  cacheKey,
}: {
  detail: SkillCatalogDetail;
  /** Mesma razão do `FilesStep`: quem usa decide de onde o arquivo vem. */
  readFile: (path: string) => Promise<{ content: string }>;
  cacheKey: unknown[];
}) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["skill_resource", ...cacheKey, detail.name, detail.license_path],
    enabled: !!detail.license_path,
    queryFn: () => readFile(detail.license_path!),
  });

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{t("skills.license.hint")}</p>

      {detail.license_path && (
        <div className="space-y-2">
          <p className="font-mono text-xs text-muted-foreground">{detail.license_path}</p>
          <div className="max-h-[45vh] overflow-y-auto rounded-md border bg-muted/20 p-4">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : isError ? (
              <p className="text-xs text-destructive">{t("skills.files.loadFailed")}</p>
            ) : (
              /* Markdown renderizado, e nao bloco de codigo: licenca e texto
                 para ler, nao fonte para inspecionar. Em monoespacado ela se
                 parecia com o conteudo do passo de Estrutura, que e onde se olha
                 arquivo — e aqui o que esta em jogo e aceitar termos.

                 Licenca costuma vir em texto puro, com quebras de linha rigidas
                 herdadas do formato de 80 colunas. O markdown as reflui em
                 paragrafos, que e como se le prosa; o texto continua integral,
                 so nao preso a largura de outra epoca. */
              <div className="prose prose-sm max-w-none break-words dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{data?.content ?? ""}</ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Estrutura: a árvore do pacote, com o arquivo escolhido ao lado (AC11).
 *
 * O conteúdo vem sob demanda, um arquivo por vez: carregar o pacote inteiro só
 * para abrir a tela desperdiçaria a maior parte, já que quase sempre se olha um
 * arquivo ou nenhum.
 */
/**
 * A linguagem que o editor usa para colorir, deduzida da extensao.
 *
 * O pacote de uma skill nao e so markdown: leva JSON de configuracao, YAML,
 * script em `scripts/`. Texto cru num `<pre>` esconde justamente o que se quer
 * conferir antes de instalar — se o JSON esta bem formado, o que o script faz.
 */
const languageOf = (path: string): string => {
  const extension = path.split(".").pop()?.toLowerCase() ?? "";

  const byExtension: Record<string, string> = {
    md: "markdown",
    markdown: "markdown",
    json: "json",
    yml: "yaml",
    yaml: "yaml",
    js: "javascript",
    mjs: "javascript",
    cjs: "javascript",
    ts: "typescript",
    py: "python",
    sh: "shell",
    bash: "shell",
    sql: "sql",
    html: "html",
    css: "css",
    xml: "xml",
    csv: "plaintext",
    txt: "plaintext",
  };

  return byExtension[extension] ?? "plaintext";
};

/**
 * A arvore de diretorios do pacote, montada a partir dos caminhos relativos.
 *
 * O pacote e uma pasta — `references/`, `examples/`, `scripts/` sao convencoes
 * do proprio padrao — e uma lista plana esconderia essa organizacao, que e parte
 * do que se quer conferir antes de instalar.
 */
interface TreeNode {
  name: string;
  /** Caminho completo do arquivo, ou parcial da pasta — nunca vazio. */
  path: string;
  /** Pasta se distingue de arquivo por aqui, e não pelo caminho estar vazio. */
  isDirectory: boolean;
  children: TreeNode[];
}

const buildTree = (paths: string[]): TreeNode[] => {
  const roots: TreeNode[] = [];

  for (const path of paths) {
    const segments = path.split("/");
    let level = roots;

    segments.forEach((segment, index) => {
      const isLeaf = index === segments.length - 1;
      const partial = segments.slice(0, index + 1).join("/");

      let node = level.find((candidate) => candidate.name === segment);
      if (!node) {
        // Toda pasta carrega o caminho parcial ate ela. Sem isso, dois arquivos
        // de mesmo nome em pastas diferentes colidiriam na chave do React, que
        // reaproveitaria a subarvore errada — `scripts/util.py` e
        // `references/util.py` sao um caso comum num pacote.
        node = { name: segment, path: partial, isDirectory: !isLeaf, children: [] };
        level.push(node);
      }
      level = node.children;
    });
  }

  const sort = (nodes: TreeNode[]): TreeNode[] =>
    nodes
      .map((node) => ({ ...node, children: sort(node.children) }))
      // ARQUIVO antes de pasta, e cada grupo em ordem alfabetica.
      //
      // O contrario e a ordem de um navegador de arquivos, mas aqui o que se
      // procura esta na raiz: o `SKILL.md` e a licenca ficavam empurrados para
      // baixo das pastas de apoio — justamente o arquivo que a pessoa abriu a
      // tela para ler.
      .sort((a, b) => {
        if (a.isDirectory !== b.isDirectory) return a.isDirectory ? 1 : -1;
        return a.name.localeCompare(b.name);
      });

  return sort(roots);
};

const FileTree = ({
  nodes,
  selected,
  onSelect,
  depth = 0,
}: {
  nodes: TreeNode[];
  selected: string;
  onSelect: (path: string) => void;
  depth?: number;
}) => (
  <ul className="space-y-0.5">
    {nodes.map((node) => {
      const isDirectory = node.isDirectory;

      return (
        // O caminho completo e a unica identidade estavel e unica no pacote.
        <li key={node.path}>
          {isDirectory ? (
            <div
              className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground"
              style={{ paddingLeft: depth * 12 + 8 }}
            >
              <Folder className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate font-mono">{node.name}</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onSelect(node.path)}
              style={{ paddingLeft: depth * 12 + 8 }}
              className={cn(
                "flex w-full items-center gap-1.5 rounded-md py-1 pr-2 text-left text-xs transition-colors",
                selected === node.path ? "bg-muted font-medium" : "hover:bg-muted/50",
              )}
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate font-mono">{node.name}</span>
            </button>
          )}

          {isDirectory && (
            <FileTree
              nodes={node.children}
              selected={selected}
              onSelect={onSelect}
              depth={depth + 1}
            />
          )}
        </li>
      );
    })}
  </ul>
);

export const FilesStep = ({
  detail,
  readFile,
  cacheKey,
  className = "h-full",
}: {
  detail: SkillCatalogDetail;
  /**
   * Como ler UM arquivo do pacote.
   *
   * Vem de quem usa, e não de um `target` fixo, porque o mesmo visualizador
   * serve a três caminhos que leem de lugares diferentes: o assistente de
   * instalação e a tela de configuração leem pelo escopo de quem olha, e a
   * administração lê pela versão escolhida, inclusive uma que não está no ar.
   * Duplicar o componente por causa disso faria as três telas divergirem na
   * primeira correção.
   */
  readFile: (path: string) => Promise<{ content: string }>;
  /** O que distingue esta leitura no cache — escopo, ou skill mais versão. */
  cacheKey: unknown[];
  /**
   * A altura vem de quem usa, e não do próprio passo.
   *
   * O editor ocupa 100% do que recebe. Dentro da janela de instalação isso é o
   * corpo do diálogo, que tem altura fixa; numa página, não há altura nenhuma a
   * herdar, e o editor colapsa para a altura do conteúdo — que foi como ele
   * apareceu com três linhas na tela de configuração.
   */
  className?: string;
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [selected, setSelected] = useState<string>(SKILL_ENTRY_FILE);

  const entry = detail.resources.find((resource) => resource.path === selected);
  const isEntryFile = selected === SKILL_ENTRY_FILE;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["skill_resource", ...cacheKey, detail.name, selected],
    // O `SKILL.md` já veio no detalhe; buscá-lo de novo seria uma ida à rede
    // para um conteúdo que a tela tem em mãos.
    enabled: !isEntryFile && !!entry && !entry.binary,
    queryFn: () => readFile(selected),
  });

  const tree = buildTree([SKILL_ENTRY_FILE, ...detail.resources.map((r) => r.path)]);

  return (
    <div className={cn("grid min-h-0 gap-4 md:grid-cols-[260px_1fr]", className)}>
      <div className="overflow-y-auto rounded-md border bg-muted/20 py-2">
        <FileTree nodes={tree} selected={selected} onSelect={setSelected} />
      </div>

      <div className="min-h-0 overflow-hidden rounded-md border">
        {entry?.binary ? (
          <p className="p-4 text-xs text-muted-foreground">{t("skills.files.binary")}</p>
        ) : isLoading ? (
          <div className="p-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : isError ? (
          <p className="p-4 text-xs text-destructive">{t("skills.files.loadFailed")}</p>
        ) : (
          // Somente leitura: aqui se confere o que vai ser instalado, e editar
          // mudaria o pacote publicado, que e imutavel por versao.
          <Editor
            height="100%"
            language={languageOf(selected)}
            value={isEntryFile ? detail.body : (data?.content ?? "")}
            theme={theme === "dark" ? "vs-dark" : "vs"}
            options={{
              readOnly: true,
              domReadOnly: true,
              minimap: { enabled: false },
              wordWrap: "on",
              fontSize: 14,
              lineHeight: 22,
              padding: { top: 16, bottom: 16 },
              scrollBeyondLastLine: false,
              lineNumbers: "on",
              folding: true,
              renderLineHighlight: "none",
              scrollbar: { alwaysConsumeMouseWheel: false },
            }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Integrações: qual das configuradas atende cada requisito (AC8, AC9).
 *
 * Usa o mesmo `IntegrationRequirement` do instalador de agentes — a área do
 * requisito que se clica para abrir o painel lateral, com busca por nome e
 * classificação — em vez de um seletor próprio: é a mesma decisão, e duas telas
 * diferentes para ela só fariam a pessoa reaprender.
 *
 * `allowedIds` é o que impede a tela de prometer o que a instalação recusa. O
 * painel lista integrações da ORGANIZAÇÃO, e a skill só aceita as que alcançam
 * ESTE workspace — sem a restrição, um Azure DevOps local de outro workspace
 * apareceria escolhível e voltaria como requisito não atendido na confirmação.
 *
 * Este passo só existe quando a skill declara requisito — ver a montagem da
 * sequência no wizard.
 */
export const IntegrationsStep = ({
  skill,
  choices,
  onToggle,
}: {
  skill: SkillStorefrontEntry;
  /** Requisito → integrações escolhidas. Lista, porque categoria aceita várias. */
  choices: Record<string, string[]>;
  onToggle: (requirement: string, integrationBaseId: string) => void;
}) => {
  const { t, i18n } = useTranslation();
  const unavailable = skill.unavailable_requirements ?? [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("skills.integrations.hint")}</p>

      {(skill.required_integrations ?? []).map((requirement) => {
        const missing = requirement.candidates.length === 0;
        const escolhidas = choices[requirement.requirement] ?? [];
        // Requisito que nomeia uma CATEGORIA aceita várias integrações (AC15):
        // a organização pode ter um board de produto e outro de engenharia, e a
        // skill vale para os dois. Requisito que nomeia um template é um só.
        const aceitaVarias = requirement.requirement.includes("classification:");

        if (missing) {
          return (
            <div
              key={requirement.requirement}
              className="flex items-center gap-3 rounded-lg border border-dashed border-amber-500/40 bg-amber-500/5 p-4"
            >
              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
              <div>
                <p className="text-sm">{requirementLabel(requirement.requirement, t)}</p>
                {textoDaSkill(requirement.reason, requirement.reason_i18n, i18n.language) && (
                  <p className="text-xs text-muted-foreground">
                    {textoDaSkill(requirement.reason, requirement.reason_i18n, i18n.language)}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t("skills.integrations.configureFirst")}
                </p>
              </div>
            </div>
          );
        }

        return (
          <div key={requirement.requirement} className="space-y-2 rounded-lg border p-4">
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-sm font-medium">{requirementLabel(requirement.requirement, t)}</p>
              <p className="text-xs text-muted-foreground">
                {aceitaVarias
                  ? t("skills.integrations.pickMany")
                  : t("skills.integrations.pickOne")}
              </p>
            </div>

            {/* Para que a skill precisa disto. Escolher uma instância sem saber
                a que ela serve no procedimento é escolher no escuro — e só o
                autor da skill sabe responder. */}
            {textoDaSkill(requirement.reason, requirement.reason_i18n, i18n.language) && (
              <p className="text-xs text-muted-foreground">
                {textoDaSkill(requirement.reason, requirement.reason_i18n, i18n.language)}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              {requirement.candidates.map((candidate) => {
                const marcada = escolhidas.includes(candidate.integration_base_id);

                return (
                  <Button
                    key={candidate.integration_base_id}
                    type="button"
                    variant={marcada ? "default" : "outline"}
                    size="sm"
                    className="gap-1"
                    onClick={() => {
                      // Requisito de template é um só: escolher outro troca, em
                      // vez de somar — somar instalaria duas credenciais para
                      // algo que a skill nomeou no singular.
                      if (!aceitaVarias && !marcada) {
                        for (const anterior of escolhidas) {
                          onToggle(requirement.requirement, anterior);
                        }
                      }
                      onToggle(requirement.requirement, candidate.integration_base_id);
                    }}
                  >
                    {marcada && <Check className="h-3.5 w-3.5" />}
                    {candidate.name}
                  </Button>
                );
              })}
            </div>
          </div>
        );
      })}

      {unavailable.length > 0 && (
        <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
          {t("skills.integrations.unavailableExplainer", { integrations: unavailable.join(", ") })}
        </p>
      )}

      <PlatformRequirements skill={skill} />
    </div>
  );
};

/**
 * Os recursos da plataforma que a skill usa — a base de conhecimento, entre
 * outros.
 *
 * Separado das integrações porque a natureza é outra: não se instalam e não há
 * o que escolher. `knowledge_search` está sempre disponível; o que varia é
 * haver conteúdo para ele devolver. Por isso **informa e não bloqueia** — é o
 * oposto do requisito de integração, que trava a instalação quando não há
 * candidata.
 *
 * Só o estado `empty` ganha destaque: é o caso em que a skill vai rodar e não
 * encontrar nada, e quem instala merece saber disso antes e não depois.
 */
const PlatformRequirements = ({ skill }: { skill: SkillStorefrontEntry }) => {
  const { t } = useTranslation();
  const recursos = skill.required_platform ?? [];

  if (recursos.length === 0) return null;

  return (
    <div className="space-y-2 border-t pt-4">
      <p className="text-sm font-medium">{t("skills.platform.title")}</p>
      <p className="text-xs text-muted-foreground">{t("skills.platform.hint")}</p>

      <ul className="space-y-1.5">
        {recursos.map((recurso) => (
          <li key={recurso.tool} className="flex items-start gap-2 text-sm">
            {recurso.readiness === "empty" ? (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-500" />
            ) : (
              <Boxes className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}
            <span className="min-w-0">
              <span className="block">
                {t(`skills.platform.tool.${recurso.tool}`, recurso.tool)}
              </span>
              {recurso.readiness === "empty" && (
                <span className="block text-xs text-amber-600 dark:text-amber-500">
                  {t(`skills.platform.empty.${recurso.tool}`, t("skills.platform.emptyGeneric"))}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Conclusão: o que vai ser instalado, em uma tela só, antes de confirmar. */
export const ReviewStep = ({
  skill,
  detail,
  choices,
  scope,
}: {
  skill: SkillStorefrontEntry;
  detail: SkillCatalogDetail;
  choices: Record<string, string[]>;
  /** Onde vai valer. Vem da navegação, e por isso a tela INFORMA em vez de perguntar (AC13). */
  scope: SkillInstallationScope;
}) => {
  const { t } = useTranslation();
  const requirements = skill.required_integrations ?? [];

  /**
   * As integrações escolhidas para um requisito, com nome e o que as distingue.
   *
   * Duas instâncias da mesma ferramenta podem ter o MESMO nome — uma organização
   * com dois Azure DevOps costuma ter os dois chamados "azuredevops". Listá-las
   * assim mostrava "azuredevops, azuredevops", e quem confirma não tinha como
   * saber qual credencial estava aceitando.
   *
   * Quando o nome se repete, o começo do id entra ao lado. Não é bonito, mas é a
   * única coisa que de fato as separa — e esconder a ambiguidade é pior do que
   * mostrá-la.
   */
  const escolhidasDe = (requirement: string) => {
    const candidatas = requirements.find((entry) => entry.requirement === requirement)?.candidates;
    const escolhidas = choices[requirement] ?? [];

    const nomes = escolhidas.map(
      (id) => candidatas?.find((c) => c.integration_base_id === id)?.name ?? id,
    );

    return escolhidas.map((id, indice) => {
      const nome = nomes[indice]!;
      const repetido = nomes.filter((outro) => outro === nome).length > 1;

      return { id, nome, distintivo: repetido ? id.slice(0, 6) : null };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-4">
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="space-y-1">
          <p className="text-sm font-medium">{detail.title || detail.name}</p>
          <p className="text-xs text-muted-foreground">
            {t("skills.review.version", { version: detail.version })}
          </p>
        </div>
      </div>

      {/* Onde a skill vai valer. INFORMA, não pergunta: o nível é o da navegação
          (AC13), e mostrá-lo aqui é o que evita instalar achando que é noutro. */}
      <div className="flex items-start gap-3 rounded-md border p-4">
        <Layers className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="space-y-1">
          <p className="text-sm font-medium">{t(`skills.scope.${scope}`)}</p>
          <p className="text-xs text-muted-foreground">{t(`skills.scope.hint.${scope}`)}</p>
        </div>
      </div>

      {/* As integrações escolhidas, no mesmo formato de cartão dos blocos acima.
          Antes eram uma lista solta embaixo, com o requisito em monoespaçado —
          parecia rodapé técnico, quando é parte do que se está confirmando. */}
      {requirements.length > 0 && (
        <div className="flex items-start gap-3 rounded-md border p-4">
          <Plug className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1 space-y-3">
            <p className="text-sm font-medium">{t("skills.integrations.title")}</p>

            {requirements.map((requirement) => {
              const escolhidas = escolhidasDe(requirement.requirement);

              return (
                <div key={requirement.requirement} className="space-y-1.5">
                  {/* O rótulo legível, e não o token declarado: `classification:project`
                      é como a skill escreve, não como se lê. */}
                  <p className="text-xs text-muted-foreground">
                    {requirementLabel(requirement.requirement, t)}
                  </p>

                  {escolhidas.length === 0 ? (
                    <p className="text-sm text-muted-foreground">—</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {escolhidas.map((escolhida) => (
                        <Badge key={escolhida.id} variant="secondary" className="gap-1 font-normal">
                          {escolhida.nome}
                          {escolhida.distintivo && (
                            <span className="font-mono text-[10px] opacity-60">
                              {escolhida.distintivo}
                            </span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* O aviso da versão fixada fecha o passo, recuado do resto: é a única
          linha aqui que não descreve uma escolha, e sim o que acontece depois. */}
      <p className="border-t pt-4 text-xs text-muted-foreground">{t("skills.review.hint")}</p>
    </div>
  );
};

/**
 * Parâmetros: o que a skill precisa saber para rodar do jeito daqui (AB#85634).
 *
 * Só existe quando a skill declara algum — a maioria não declara, e um passo
 * vazio seria uma parada sem decisão.
 *
 * É o mesmo formato das integrações: a skill declara o requisito no pacote, e a
 * instalação responde uma vez. O ganho é evitar o fork — um time que prioriza
 * por RICE e outro por WSJF usam a MESMA skill, com instalações diferentes, em
 * vez de duas cópias que divergem na primeira correção.
 */
export const ParametersStep = ({
  parameters,
  values,
  onChange,
}: {
  parameters: SkillParameter[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("skills.parameters.hint")}</p>

      {parameters.map((parametro) => {
        const valor = values[parametro.key] ?? parametro.default ?? "";
        const vazioObrigatorio = parametro.required && !valor;

        return (
          <div key={parametro.key} className="space-y-2 rounded-lg border p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <label htmlFor={`param-${parametro.key}`} className="text-sm font-medium">
                {textoDaSkill(parametro.label, parametro.label_i18n, i18n.language)}
              </label>
              {parametro.required && (
                <span className="text-xs text-muted-foreground">
                  {t("skills.parameters.required")}
                </span>
              )}
            </div>

            {textoDaSkill(parametro.help, parametro.help_i18n, i18n.language) && (
              <p className="text-xs text-muted-foreground">
                {textoDaSkill(parametro.help, parametro.help_i18n, i18n.language)}
              </p>
            )}

            {/* Lista fechada vira BOTÕES, e não campo de texto: o valor entra no
                que o agente lê, e uma lista fechada não tem a borda de injeção
                que o texto livre tem. */}
            {parametro.type === "enum" ? (
              <div className="flex flex-wrap gap-2">
                {parametro.options.map((opcao) => (
                  <Button
                    key={opcao}
                    type="button"
                    variant={valor === opcao ? "default" : "outline"}
                    size="sm"
                    className="gap-1"
                    onClick={() => onChange(parametro.key, opcao)}
                  >
                    {valor === opcao && <Check className="h-3.5 w-3.5" />}
                    {opcao}
                  </Button>
                ))}
              </div>
            ) : parametro.type === "boolean" ? (
              <div className="flex gap-2">
                {(["true", "false"] as const).map((opcao) => (
                  <Button
                    key={opcao}
                    type="button"
                    variant={valor === opcao ? "default" : "outline"}
                    size="sm"
                    onClick={() => onChange(parametro.key, opcao)}
                  >
                    {t(`skills.parameters.${opcao === "true" ? "yes" : "no"}`)}
                  </Button>
                ))}
              </div>
            ) : (
              <Input
                id={`param-${parametro.key}`}
                type={parametro.type === "number" ? "number" : "text"}
                value={valor}
                onChange={(event) => onChange(parametro.key, event.target.value)}
                className={cn("h-9", vazioObrigatorio && "border-amber-500/60")}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
